(() => {
if (window.SupabasePaletteAPI) return;

/**
 * Supabase Client for User Palettes
 * Handles CRUD operations for user-owned color palettes
 *
 * Note: This file expects the Supabase JS library to be loaded first
 * Include via CDN: <script src="https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2"></script>
 */

const SUPABASE_URL = "https://ddsqshuonytkwbgkrzrl.supabase.co";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRkc3FzaHVvbnl0a3diZ2tyenJsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTkyOTg5OTQsImV4cCI6MjA3NDg3NDk5NH0.uo2Ivt-b2yX3c_seclFUC2QaxGof6KfgXhPT9pBvFjk";

class SupabasePaletteAPI {
  constructor() {
    // Initialize Supabase client using the official library
    if (typeof window.supabase === "undefined") {
      console.error(
        "Supabase library not loaded. Include it before this script."
      );
      throw new Error("Supabase library not loaded");
    }
    this.tableName = "palettes";
  }

  getClient(customerId) {
    if (customerId != this.customerId || !this.client) {
      this.createSupabaseClient(customerId);
    }
    return this.client;
  }

  createSupabaseClient(customerId) {
    this.customerId = customerId;
    this.client = window.supabase.createClient(
      SUPABASE_URL,
      SUPABASE_ANON_KEY,
      {
        global: {
          headers: { "x-customer-id": customerId },
        },
      }
    );
  }

  /**
   * Get all palettes for a customer
   * @param {number} customerId - Shopify customer ID
   * @returns {Promise<Array>} Array of palette objects
   */
  async getPalettes(customerId, signal) {
    if (!customerId) {
      throw new Error("Customer ID is required");
    }

    const client = this.getClient(customerId);

    const query = client.from(this.tableName).select("*").order("created_at", { ascending: false });
    if (signal) query.abortSignal(signal);
    const { data, error, status } = await query;

    if (error) {
      console.error("Error fetching palettes:", error);
      throw Object.assign(new Error(error.message), {
        retryable: !status || status === 429 || status >= 500,
      });
    }

    return data || [];
  }

  /**
   * Create a new palette
   * @param {number} customerId - Shopify customer ID
   * @param {string} name - Palette name
   * @param {string} colors - Colors as comma-separated string (e.g., "#FF0000,#00FF00")
   * @returns {Promise<Object>} Created palette object
   */
  async createPalette(customerId, name, colors) {
    if (!customerId || !name || !colors) {
      throw new Error("Customer ID, name, and colors are required");
    }

    const client = this.getClient(customerId);
    const { data, error } = await client
      .from(this.tableName)
      .insert([
        {
          customer_id: customerId,
          name: name,
          colors: colors,
        },
      ])
      .select();

    if (error) {
      console.error("Error creating palette:", error);
      throw new Error(error.message);
    }

    return data[0];
  }

  /**
   * Update an existing palette
   * @param {number} paletteId - Palette ID
   * @param {number} customerId - Shopify customer ID (for validation)
   * @param {Object} updates - Object with fields to update (name and/or colors)
   * @returns {Promise<Object>} Updated palette object
   */
  async updatePalette(paletteId, customerId, updates) {
    if (!paletteId || !customerId) {
      throw new Error("Palette ID and Customer ID are required");
    }

    const client = this.getClient(customerId);
    const { data, error } = await client
      .from(this.tableName)
      .update(updates)
      .eq("id", paletteId)
      .eq("customer_id", customerId)
      .select();

    if (error) {
      console.error("Error updating palette:", error);
      throw new Error(error.message);
    }

    return data?.[0] || { success: true, id: paletteId, ...updates };
  }

  /**
   * Delete a palette
   * @param {number} paletteId - Palette ID
   * @param {number} customerId - Shopify customer ID (for validation)
   * @returns {Promise<Object>} Result object
   */
  async deletePalette(paletteId, customerId) {
    if (!paletteId || !customerId) {
      throw new Error("Palette ID and Customer ID are required");
    }

    const client = this.getClient(customerId);
    const { data, error } = await client
      .from(this.tableName)
      .delete()
      .eq("id", paletteId)
      .eq("customer_id", customerId)
      .select();

    if (error) {
      console.error("Error deleting palette:", error);
      throw new Error(error.message);
    }

    return { success: true, id: paletteId };
  }
}

// Create a singleton instance
window.SupabasePaletteAPI =
  window.SupabasePaletteAPI || new SupabasePaletteAPI();

})();
